import { Component, Input, Output, EventEmitter, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { Subscription, interval, timer } from 'rxjs';
import { switchMap, takeWhile, takeUntil } from 'rxjs/operators';
import { PaymentMethodModel, CardType } from '../../../../domain/models/payment.model';
import { ReturnSummaryDto } from '../../../../domain/models/return.model';
import { GatewayService } from '../../../../domain/services/gateway/gateway.service';
import { GatewayTerminal, GatewayTxnStatus } from '../../../../domain/models/gateway.model';

/** State machine for the POS terminal flow. No card-entry UI exists anywhere — PCI scope
 *  stays off this app entirely; MOCK and real processors both go through a real terminal
 *  (the mock backend just registers fake devices like "Front Desk (mock)"). */
export type TerminalFlowState = 'idle' | 'sending' | 'waiting' | 'approved' | 'failed';

const POLL_INTERVAL_MS = 2000;
const POLL_DEADLINE_MS = 180_000;

@Component({
  selector: 'app-payment-method-debit',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatButtonModule,
    MatIconModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatProgressSpinnerModule
  ],
  templateUrl: './payment-method-debit.component.html',
  styleUrls: ['./payment-method-debit.component.css']
})
export class PaymentMethodDebitComponent implements OnInit, OnDestroy {
  @Input() method!: PaymentMethodModel;
  /** Account being paid — required by GatewayService.initiate(). */
  @Input() accountId!: number;
  /** Returns in the basket, used to build the POS cart line items. */
  @Input() returns: ReturnSummaryDto[] = [];
  @Output() removed = new EventEmitter<string>();
  @Output() amountChanged = new EventEmitter<{ method: PaymentMethodModel; amount: number }>();
  /** Full replacement of the method (gateway fields filled in on approval). */
  @Output() methodUpdated = new EventEmitter<PaymentMethodModel>();

  terminals: GatewayTerminal[] = [];
  loadingTerminals: boolean = false;
  selectedDeviceId: string | null = null;
  flowState: TerminalFlowState = 'idle';
  transactionId: string | null = null;
  approvedStatus: GatewayTxnStatus | null = null;
  errorMessage: string = '';

  private pollSub?: Subscription;

  constructor(private gatewayService: GatewayService) {}

  ngOnInit(): void {
    this.selectedDeviceId = this.gatewayService.selectedTerminal;
    this.loadTerminals(false);
  }

  ngOnDestroy(): void {
    this.pollSub?.unsubscribe();
  }

  onRemove(): void {
    this.removed.emit(this.method.id);
  }

  loadTerminals(refresh: boolean): void {
    this.loadingTerminals = true;
    this.gatewayService.getTerminals(refresh).subscribe({
      next: list => {
        this.terminals = list;
        this.loadingTerminals = false;
      },
      error: () => {
        this.loadingTerminals = false;
      }
    });
  }

  onRefreshTerminals(): void {
    this.loadTerminals(true);
  }

  onTerminalChange(deviceId: string): void {
    this.selectedDeviceId = deviceId;
    this.gatewayService.selectedTerminal = deviceId;
  }

  /** Cart line total must equal the amount actually charged. Full basket → per-return
   *  lines; split tender (card covers less than the whole basket) → a single summary
   *  line for the charged amount so Catalis's own total matches ours. */
  private buildCart(): { itemId: string; description: string; amount: number }[] {
    const basketTotal = this.returns.reduce((sum, r) => sum + r.totalAmountDue, 0);
    if (Math.abs(this.method.amount - basketTotal) <= 0.005) {
      return this.returns.map(r => ({
        itemId: String(r.id),
        description: `${r.type} ${r.period}`,
        amount: r.totalAmountDue
      }));
    }
    const accountNumber = this.returns[0]?.customerId ?? String(this.accountId);
    return [{
      itemId: String(this.accountId),
      description: `Partial payment — account ${accountNumber}`,
      amount: this.method.amount
    }];
  }

  onSendToTerminal(): void {
    if (!this.selectedDeviceId) {
      return;
    }
    this.flowState = 'sending';
    this.errorMessage = '';
    this.gatewayService
      .initiate(this.selectedDeviceId, this.accountId, this.method.amount, this.buildCart())
      .subscribe({
        next: r => {
          this.transactionId = r.transactionId;
          this.flowState = 'waiting';
          this.startPolling();
        },
        error: e => {
          this.flowState = 'failed';
          this.errorMessage = e.error?.error ?? 'Terminal unavailable';
        }
      });
  }

  /** Polls every 2s. `takeUntil` enforces an absolute 180s deadline — `timeout()` alone
   *  would reset on every emission and never fire against a poll that keeps returning
   *  Pending forever. Hitting the deadline while still 'waiting' is treated as a timeout. */
  private startPolling(): void {
    this.pollSub = interval(POLL_INTERVAL_MS)
      .pipe(
        switchMap(() => this.gatewayService.txnStatus(this.transactionId!)),
        takeWhile(s => s.status === 'Pending', true), // emit the terminal status too
        takeUntil(timer(POLL_DEADLINE_MS))
      )
      .subscribe({
        next: s => {
          if (s.status === 'Approved') {
            this.flowState = 'approved';
            this.approvedStatus = s;
            this.emitMethod(s);
          } else if (s.status !== 'Pending') {
            this.flowState = 'failed';
            this.errorMessage = s.message ?? `Transaction ${s.status}`;
          }
        },
        error: () => {
          this.flowState = 'failed';
          this.errorMessage = 'Lost connection to terminal';
        },
        complete: () => this.handlePollComplete()
      });
  }

  /** Fires on any completion — normal (Approved/Failed via takeWhile) or deadline
   *  (takeUntil). Only the deadline case leaves flowState still 'waiting'. */
  private handlePollComplete(): void {
    if (this.flowState !== 'waiting') {
      return;
    }
    this.flowState = 'failed';
    this.errorMessage = 'Timed out waiting for terminal';
    if (this.transactionId) {
      this.gatewayService.abort(this.transactionId, 'Timed out').subscribe();
    }
  }

  /** Emit the method enriched with the approved gateway result. Fee is always explicit 0 —
   *  Catalis keeps its own processing fee, the county only records the principal. (Must be
   *  the literal number 0, not undefined — the backend defaults a null fee % to 2%.) */
  private emitMethod(status: GatewayTxnStatus): void {
    const updated: PaymentMethodModel = {
      ...this.method,
      cardLastFour: status.last4 ?? undefined,
      cardType: (status.cardBrand as CardType) ?? undefined,
      cardFeePercentage: 0,
      cardFeeAmount: 0,
      gatewayTransactionId: status.transactionId
    };
    this.method = updated;
    this.methodUpdated.emit(updated);
  }

  onCancel(): void {
    this.pollSub?.unsubscribe();
    if (this.transactionId) {
      this.gatewayService.abort(this.transactionId, 'Cancelled by clerk').subscribe();
    }
    this.flowState = 'idle';
    this.transactionId = null;
  }

  onTryAgain(): void {
    this.flowState = 'idle';
    this.errorMessage = '';
    this.transactionId = null;
    this.approvedStatus = null;
  }
}
